import React from 'react';
import Button from '../../../components/Button';
import FontAwesomeIcon from '../../../components/Icon';
import Tooltip from 'src/components/Tooltip/Tooltip';
import { BulkUpdateServiceRequest } from './BulkUpdateServiceRequest';
import { isRole } from 'src/helpers/permissions';
import { USER_ROLES } from 'src/common/constants';
import { useSelector } from 'react-redux';
import ManageVisibilityButton from 'src/pages/ServiceRequests/components/ManageVisibilityButton';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import './ServiceRequestDashboardActions.scss';
import Search from 'src/components/Search/Search';
import useScreenSize from 'src/SharedCustomHooks/UseScreenSize/useScreenSize';

const SUBSCRIBED_ONLY_TOOLTIP_CONTENT: string =
  "Show only service requests you're subscribed to for email notifications to but are not the primary assignee.";

interface IServiceRequestDashboardActionsProps {
  searchString: string;
  onChangeSearchString: (value: string) => void;
  onShowFilterOverlay?: () => void;
  filterCount?: number;
  disableGroup?: boolean;
  onGroup?: () => void;
  onBulkStatusUpdate?: (status: string) => void;
  onShowColumnSelectionDialog?: () => void;
  showResolved?: boolean;
  onShowResolved?: () => void;
  onDownload: () => void;
  selectedCount?: number;
  showSubscribedOnly?: boolean;
  onToggleShowSubscribedOnly?: () => void;
  hideBulkActions?: boolean;
  hideGroupButton?: boolean;
  hideShowResolved?: boolean;
  onManageVisibility?: () => void;
}

export const ServiceRequestDashboardActions = ({
  searchString,
  onChangeSearchString,
  onShowFilterOverlay,
  filterCount,
  disableGroup = false,
  onGroup,
  onBulkStatusUpdate,
  onShowColumnSelectionDialog,
  showResolved,
  onShowResolved,
  onDownload,
  selectedCount = 0,
  showSubscribedOnly = false,
  onToggleShowSubscribedOnly,
  hideBulkActions = false,
  hideGroupButton = false,
  hideShowResolved = false,
  onManageVisibility,
}: IServiceRequestDashboardActionsProps) => {
  const user = useSelector(selectAuthenticatedUser);

  // Column options (Adjust Columns) are an internal management action — only
  // Property Developers, Admins, and Super Admins see the button at all.
  const canManageColumns: boolean = isRole(
    [
      USER_ROLES.PROPERTY_DEVELOPER,
      USER_ROLES.ADMIN,
      USER_ROLES.SUPER_ADMIN,
    ],
    user
  );

  const { width } = useScreenSize();

  const isStacked: boolean = width < 1800;

  if (isStacked) {
    return (
      <div className="service-request-dashboard-actions-stacked">
        <div className="serviceRequestDashboardActions__search">
          <Search
            size="small"
            placeholder="Search Service Requests..."
            value={searchString}
            onChange={(c) => onChangeSearchString(c.target.value)}
            theme={undefined}
          />
        </div>
        <div className="service-request-dashboard-actions-buttons-row">
          {!hideShowResolved && onShowResolved && (
            <Button
              color="teal"
              label="Show Resolved"
              size="small"
              onClick={onShowResolved}
              icon={<FontAwesomeIcon glyph="check" weight="light" size="small" />}
              outlined={!showResolved ? true : undefined}
              block={undefined}
              disabled={undefined}
              outline={undefined}
              weight={undefined}
              children={undefined}
              style={undefined}
              hideLabel={false}
              id="serviceRequestShowResolvedButton"
            />
          )}
          {onToggleShowSubscribedOnly && (
            <Tooltip content={SUBSCRIBED_ONLY_TOOLTIP_CONTENT}>
              <Button
                color="teal"
                label="Subscribed Only"
                size="small"
                onClick={onToggleShowSubscribedOnly}
                icon={<FontAwesomeIcon glyph="bell" weight="light" size="small" />}
                outlined={!showSubscribedOnly ? true : undefined}
                block={undefined}
                disabled={undefined}
                outline={undefined}
                weight={undefined}
                children={undefined}
                style={undefined}
                hideLabel={false}
                id="serviceRequestSubscribedOnlyButton"
              />
            </Tooltip>
          )}
          {onShowFilterOverlay && (
            <Button
              color="teal"
              label={`Filter ${(filterCount ?? 0) > 0 ? `(${filterCount})` : ''}`}
              size="small"
              onClick={onShowFilterOverlay}
              icon={<FontAwesomeIcon glyph="filter" weight="light" size="small" />}
              block={undefined}
              disabled={undefined}
              outlined={undefined}
              outline={undefined}
              weight={undefined}
              children={undefined}
              style={undefined}
              hideLabel={false}
              id="serviceRequestFilterButton"
            />
          )}
          {!hideBulkActions &&
            !isRole(['Home Owner', 'Realtor', 'Vendor'], user) && (
              <>
                {!hideGroupButton && (
                  <Button
                    color="teal"
                    disabled={disableGroup}
                    onClick={onGroup}
                    label="Group"
                    size="small"
                    block={undefined}
                    outlined={undefined}
                    icon={undefined}
                    outline={undefined}
                    weight={undefined}
                    children={undefined}
                    style={undefined}
                  />
                )}
                <BulkUpdateServiceRequest
                  disableBulkUpdate={disableGroup}
                  onChangeStatus={onBulkStatusUpdate}
                  selectedCount={selectedCount}
                  label="UPDATE"
                  style={{ marginRight: 0 }}
                />
              </>
            )}
          {onManageVisibility && (
            <ManageVisibilityButton onClick={onManageVisibility} />
          )}
          {onShowColumnSelectionDialog && canManageColumns && (
            <Button
              color="teal"
              label="Options"
              size="small"
              onClick={onShowColumnSelectionDialog}
              icon={<FontAwesomeIcon glyph="sliders-h" weight="light" size="small" />}
              block={undefined}
              disabled={undefined}
              outlined={undefined}
              outline={undefined}
              weight={undefined}
              children={undefined}
              style={{ marginRight: '1rem' }}
            />
          )}
          <div
            onClick={disableGroup ? undefined : onDownload}
            id="serviceRequestDownloadButton"
            style={{
              opacity: disableGroup ? 0.4 : 1,
              cursor: disableGroup ? 'not-allowed' : 'pointer',
              pointerEvents: disableGroup ? 'none' : 'auto',
            }}
          >
            <FontAwesomeIcon glyph="download" weight="regular" size="big" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="serviceRequestDashboardActionsContainer">
      <div className="serviceRequestDashboardActions__search">
        <Search
          size="small"
          placeholder="Search Service Requests..."
          value={searchString}
          onChange={(c) => onChangeSearchString(c.target.value)}
          theme={undefined}
        />
      </div>
      {!hideShowResolved && onShowResolved && (
        <Button
          color="teal"
          label="Show Resolved"
          size="small"
          onClick={onShowResolved}
          icon={<FontAwesomeIcon glyph="check" weight="light" size="small" />}
          outlined={!showResolved ? true : undefined}
          block={undefined}
          disabled={undefined}
          outline={undefined}
          weight={undefined}
          children={undefined}
          style={undefined}
          hideLabel={false}
          id="serviceRequestShowResolvedButton"
        />
      )}
      {onToggleShowSubscribedOnly && (
        <Tooltip content={SUBSCRIBED_ONLY_TOOLTIP_CONTENT}>
          <Button
            color="teal"
            label="Subscribed Only"
            size="small"
            onClick={onToggleShowSubscribedOnly}
            icon={<FontAwesomeIcon glyph="bell" weight="light" size="small" />}
            outlined={!showSubscribedOnly ? true : undefined}
            block={undefined}
            disabled={undefined}
            outline={undefined}
            weight={undefined}
            children={undefined}
            style={undefined}
            hideLabel={false}
            id="serviceRequestSubscribedOnlyButton"
          />
        </Tooltip>
      )}
      {onShowFilterOverlay && (
        <Button
          color="teal"
          label={`Filter ${(filterCount ?? 0) > 0 ? `(${filterCount})` : ''}`}
          size="small"
          onClick={onShowFilterOverlay}
          icon={<FontAwesomeIcon glyph="filter" weight="light" size="small" />}
          block={undefined}
          disabled={undefined}
          outlined={undefined}
          outline={undefined}
          weight={undefined}
          children={undefined}
          style={undefined}
          hideLabel={false}
          id="serviceRequestFilterButton"
        />
      )}

      {!hideBulkActions &&
        !isRole(['Home Owner', 'Realtor', 'Vendor'], user) && (
          <>
            {!hideGroupButton && (
              <Button
                color="teal"
                disabled={disableGroup}
                onClick={onGroup}
                label="Group"
                size="small"
                block={undefined}
                outlined={undefined}
                icon={undefined}
                outline={undefined}
                weight={undefined}
                children={undefined}
                style={undefined}
              />
            )}
            <BulkUpdateServiceRequest
              disableBulkUpdate={disableGroup}
              onChangeStatus={onBulkStatusUpdate}
              selectedCount={selectedCount}
              label="UPDATE"
              style={{ marginRight: 0 }}
            />
          </>
        )}
      {onManageVisibility && (
        <ManageVisibilityButton onClick={onManageVisibility} />
      )}
      {onShowColumnSelectionDialog && canManageColumns && (
        <Button
          color="teal"
          label="Options"
          size="small"
          onClick={onShowColumnSelectionDialog}
          icon={
            <FontAwesomeIcon glyph="sliders-h" weight="light" size="small" />
          }
          block={undefined}
          disabled={undefined}
          outlined={undefined}
          outline={undefined}
          weight={undefined}
          children={undefined}
          style={{ marginRight: '1rem' }}
        />
      )}
      <div
        onClick={disableGroup ? undefined : onDownload}
        id="serviceRequestDownloadButton"
        style={{
          opacity: disableGroup ? 0.4 : 1,
          cursor: disableGroup ? 'not-allowed' : 'pointer',
          pointerEvents: disableGroup ? 'none' : 'auto',
        }}
      >
        <FontAwesomeIcon glyph="download" weight="regular" size="big" />
      </div>
    </div>
  );
};
